import { FCM } from "@capacitor-community/fcm";
import { Capacitor } from "@capacitor/core";
import SendbirdChat, { SendbirdError, SessionHandler } from "@sendbird/chat";
import { OpenChannelModule } from "@sendbird/chat/openChannel";
import { PollModule } from "@sendbird/chat/poll";
import * as Sentry from "@sentry/browser";
import React, { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { debug } from "../App";
import { useApplication } from "../contexts/ApplicationContext";
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import ApiService from "../services/ApiService";
import { MemberInterface } from "../types";

const SendbirdConnector = () => {
  const application = useApplication();
  const history = useHistory();
  const { member, setMember, deviceToken } = useContext(AuthenticatedUserContext);

  useEffect(() => {
    const init = async () => {
      debug("SendbirdConnector.init: init");
      debug("SendbirdConnector.init: application", application);

      if (!member) {
        debug("SendbirdConnector.init: no member, returning");
        return;
      }

      debug("SendbirdConnector.init: user = ", { id: member.user_id, sendbird_token: member.sendbird_token });
      debug("SendbirdConnector.init: connecting to Sendbird", application.application.sendbird_app_id);

      const params = {
        appId: application.application.sendbird_app_id,
        modules: [new OpenChannelModule(), new PollModule()],
        localCacheEnabled: true,
      };

      const getNewSessionToken = async () => {
        const memberData: MemberInterface = await ApiService.post(`members/${member.id}/sendbird/token/refresh`);

        member.sendbird_token = memberData.sendbird_token;
        setMember(member);

        debug("Got a new Sendbird token, passing back to Sendbird Session Handler");

        return memberData.sendbird_token;
      };

      //  Initialise Sendbird SDK
      const sb = SendbirdChat.init(params);
      //  Sendbird session handler - refreshes session token etc.
      const sessionHandler = new SessionHandler();
      let isInvalidSessionToken = false;

      sessionHandler.onSessionTokenRequired = (onSuccess, onError) => {
        debug("SendbirdConnector.init: refreshing sendbird_token");
        isInvalidSessionToken = true;

        getNewSessionToken().then((token) => {
          debug("Sendbird got a new session token, calling onSuccess");
          onSuccess(token);
        });
      };
      sessionHandler.onSessionClosed = () => {
        debug("SendbirdConnector.init: sendbird session closed");

        if (isInvalidSessionToken) {
          debug("Had an invalid session token, got a new one, reconnecting...");
        } else {
          debug("Session closed, but unsure why. Attempting to reconnect...");
        }

        connect();
        isInvalidSessionToken = false;
      };

      sb.setSessionHandler(sessionHandler);

      const connect = async () => {
        //  Connect to Sendbird servers
        return await sb
          .connect(String(member.id), String(member.sendbird_token))
          .then(async () => {
            debug("SendbirdConnector.init: sendbird connected with user id and token:", member.user_id, String(member.sendbird_token));
            registerDeviceTokenWithSendbird(sb);
          })
          .catch((err: SendbirdError) => {
            debug("SendbirdConnector.init: sendbird couldn't connect, error = ", err);

            if (err.code === 400303) {
              // invalid session token
              isInvalidSessionToken = true;
              getNewSessionToken().then((token) => {
                connect();
              });
            } else {
              debug("SendbirdConnector.init: FATAL SENDBIRD ERROR: ", JSON.stringify(err));
              throw err;
            }
          });
      };

      await connect();
    };

    const registerDeviceTokenWithSendbird = (sb: SendbirdChat) => {
      if (deviceToken) {
        const platform = Capacitor.getPlatform();

        const callback = (resp: any) => {
          debug("SendbirdConnector.init: registered token", deviceToken, resp);
        };

        const errorCallback = (err: any) => {
          debug("SendbirdConnector.init: error registering token", deviceToken, err);

          try {
            Sentry.captureException(err);
          } catch (e) {
            // don't crash the app if Sentry fails
          }
        };

        if (platform === "android") {
          sb.registerFCMPushTokenForCurrentUser(deviceToken).then(callback).catch(errorCallback);
        } else if (platform === "ios") {
          FCM.getToken()
            .then(({ token }) => {
              sb.registerFCMPushTokenForCurrentUser(token).then(callback).catch(errorCallback);
            })
            .catch((err: any) => {
              debug("Couldn't get FCM token for registration in SendbirdConnector", err);
            });
        }
      }
    };

    init();
  }, [application, history, member, setMember, deviceToken]);

  return <></>;
};

export default SendbirdConnector;
